import { getRandomElement, getRandomInt } from './mathMethods';
import {
    GAME_STATUSES, EGGS_TYPES, EGGS_SET, ANALYTICS
} from './constants';
import { ee } from '../eventEmitter';
import data from '../../data/basic.json';
import { analyticsEvent } from '../analytics';

const { gameSettings } = data;

const SETTINGS = {
    branchesCount: 4,
    slotsCount: 10,

    initLivesCount: gameSettings.livesCount,
    initScore: 0,
    maxScore: gameSettings.maxScore,
    // через сколько очков начинать увеличивать уровень
    // riceScorePeriod: 5,
    riceScorePeriod: 5,
    // скорость движения шарика (количество веток в секунду) (0.3)
    initSpeed: 0.3,
    // на сколько увеличивать скорость
    riceSpeedCoef: 0.02,
    // коэйффициент для вычисления скорости добавления нового яйца (относильено основной скорости)
    initAddEggSpeedCoef: 0.2,
    // период с коротым идет обратный отсчет перед началом игры (мс)
    beforePlayPeriod: 800,
    // количество единиц обратного отсчета
    initCountdown: 3
};

class Model {
    // * неизменяемые параметры
    // eslint-disable-next-line
    get branchesCount() {
        return SETTINGS.branchesCount;
    }
    // eslint-disable-next-line
    get slotsCount() {
        return SETTINGS.slotsCount;
    }

    // * базовые настройки
    // оставшееся количество жизней
    #livesCount = SETTINGS.initLivesCount;
    get livesCount() {
        return this.#livesCount;
    }

    set livesCount(val) {
        if (val > 0) {
            this.#livesCount = val;
        } else {
            this.#livesCount = 0;
            this.queue.push(this.endGame);
        }
    }

    // текущий счет
    #score = 0;
    get score() {
        return this.#score;
    }

    set score(val) {
        if (val < SETTINGS.maxScore) {
            if (val - this.#lastScoreValue > SETTINGS.riceScorePeriod) {
                this.#speed += SETTINGS.riceSpeedCoef;
                this.#lastScoreValue = val;
            }
            this.#score = val;
        } else {
            this.#score = SETTINGS.maxScore;

            this.queue.push(this.endGame);
        }
    }

    // последнее количество баллов после увеличения уровня
    #lastScoreValue;

    #gameStatus;
    get gameStatus() {
        return this.#gameStatus;
    }

    set gameStatus(val) {
        this.#gameStatus = val;
    }

    // * обновление
    // Время последнего обновелния игры (в милисекунлах)
    #lastUpdate;

    // тики игры
    #speed;
    // расстояние между слотами (часть ветки)
    #slotDistans = 1 / SETTINGS.slotsCount;
    // время, за которое шарик переходит от одного слота к другому (в милисекундах) (период обновления игры)
    get #period() {
        return (this.#slotDistans / this.#speed) * 1000;
    }

    // таймер перед стартом игры
    #beforePlayPeriod = SETTINGS.beforePlayPeriod;
    #countdown;
    get countdown() {
        return this.#countdown;
    }

    set countdown(val) {
        if (val <= 0) {
            this.play();
        } else {
            this.#countdown = val;
        }
    }

    #countdownUpdated = false;
    get countdownUpdated() {
        return this.#countdownUpdated;
    }

    // добавление яиц
    // Время добавления последнего яйца (милисекунды)
    #lastCreateEgg;
    #addEggSpeedCoef;
    get #addEggSpeed() {
        return this.#speed * this.#addEggSpeedCoef;
    }

    //  Время между добавлением новых яиц (в милисекундах)
    get #addEggPeriod() {
        return (this.#slotDistans / this.#addEggSpeed) * 1000;
    }

    // * логика обновления веток
    /* состояния веток
        [
            [null, null, egg, null ... n слотов в ветке],
            [egg, null, null, egg ...]
            ...
            n веток
        ]
        */
    #branchesCondition = new Array(SETTINGS.branchesCount).fill(null);
    get branchesCondition() {
        return this.#branchesCondition;
    }

    // массив индексов измененных веток
    #branchesIndexChanges;
    get branchesIndexChanges() {
        return this.#branchesIndexChanges;
    }

    // * подсчет результатов
    /*  упавшее яйцо
        {
            type,
            branchIndex
        }
    */
    #fallenEgg;
    get fallenEgg() {
        return this.#fallenEgg;
    }

    // пойманное яйцо (аналогично с упавшим)
    #takenEgg;
    get takenEgg() {
        return this.#takenEgg;
    }

    // * персонаж
    // положение персонажа - индекс ветки - значения 0,1,2,3
    #characterPosition = 0;
    get characterPosition() {
        return this.#characterPosition;
    }

    // * только для класса model
    // события, которые должны вызваться при следующем тике игры
    queue = [];

    // analytics
    #analyticsScore = 0;
    #analyticsScorePeriod = 10;
    #accentEvent = false;
    get accentEvent() {
        return this.#accentEvent;
    }

    constructor() {
        this.fillBranchesCondition();
        this.refreshFlags();
    }

    fillBranchesCondition() {
        for (let branchIndex = 0; branchIndex < SETTINGS.branchesCount; branchIndex += 1) {
            this.#branchesCondition[branchIndex] = new Array(SETTINGS.slotsCount).fill(EGGS_TYPES.UNSET);
        }
    }

    refreshCountdown() {
        this.#countdown = SETTINGS.initCountdown;
        this.#countdownUpdated = true;
    }

    refreshTiming() {
        this.#lastUpdate = Date.now();
        this.#lastCreateEgg = Date.now();
    }

    refreshAnalytics() {
        this.#analyticsScore = 0;
        this.#accentEvent = false;
    }

    refreshFlags() {
        this.#branchesIndexChanges = [];
        this.#fallenEgg = null;
        this.#takenEgg = null;
    }

    initValues() {
        this.refreshCountdown();
        this.#livesCount = SETTINGS.initLivesCount;
        this.#score = SETTINGS.initScore;
        this.#lastScoreValue = SETTINGS.initScore;
        this.#characterPosition = 0;

        this.#countdown = SETTINGS.initCountdown;
        this.#countdownUpdated = true;

        this.#speed = SETTINGS.initSpeed;

        this.#addEggSpeedCoef = SETTINGS.initAddEggSpeedCoef;

        this.refreshTiming();

        if (SETTINGS.initCountdown > 0) {
            this.playBeforeGame();
        } else {
            this.play();
        }
    }

    startGame() {
        this.refreshFlags();
        this.initValues();
        this.changeCharacterPosition(0);
    }

    restartGame() {
        this.fillBranchesCondition();
        this.refreshFlags();
        this.refreshCountdown();
        this.initValues();
        this.refreshAnalytics();
    }

    continueGame() {
        this.refreshCountdown();
        this.refreshTiming();

        this.#gameStatus = GAME_STATUSES.BEFORE_PLAY;
        ee.emit(GAME_STATUSES.BEFORE_PLAY);
    }

    play(params) {
        this.gameStatus = GAME_STATUSES.PLAY;
        ee.emit(GAME_STATUSES.PLAY, params);
    }

    endGame() {
        analyticsEvent('game', 'game over', `score = ${this.#score}`);
        this.setIntervalScoreEvent();
        this.gameStatus = GAME_STATUSES.WIN;
        ee.emit(GAME_STATUSES.END, this.#score);
    }

    pause() {
        ee.emit(GAME_STATUSES.PAUSE);
        this.gameStatus = GAME_STATUSES.PAUSE;
    }

    changeOrientation(orientation) {
        ee.emit('changeOrientation', orientation);
        if (this.gameStatus !== GAME_STATUSES.PAUSE) this.gameStatus = GAME_STATUSES.PAUSE;
    }

    playBeforeGame() {
        this.#gameStatus = GAME_STATUSES.BEFORE_PLAY;
        ee.emit(GAME_STATUSES.BEFORE_PLAY);
    }

    // игровая логика
    update() {
        // ! not work
        // this.updatePlay();

        // ! work
        // eslint-disable-next-line
        switch (this.#gameStatus) {
            case GAME_STATUSES.BEFORE_PLAY:
                this.beforePlayUpdate();

                break;
            case GAME_STATUSES.PLAY:
                this.updatePlay();

                break;
        }
    }

    beforePlayUpdate() {
        if (this.countdown < SETTINGS.initCountdown) this.#countdownUpdated = false;
        if (Date.now() - this.#lastUpdate > this.#beforePlayPeriod) {
            this.#lastUpdate = Date.now();
            this.countdown -= 1;
            this.#countdownUpdated = true;
        }
    }

    updatePlay() {
        this.refreshFlags();

        if (Date.now() - this.#lastUpdate > this.#period) {
            if (this.queue.length) {
                this.queue.forEach((f) => {
                    f.apply(this);
                });
                this.queue = [];
            }

            this.#lastUpdate = Date.now();
            this.checkEggDropTake();
            this.updateBranches();

            if (Date.now() - this.#lastCreateEgg > this.#addEggPeriod) {
                this.#lastCreateEgg = Date.now();
                this.addEgg();
            }
        }
    }

    updateBranches() {
        for (let branchIndex = 0; branchIndex < this.#branchesCondition.length; branchIndex += 1) {
            this.#branchesCondition[branchIndex].unshift(EGGS_TYPES.UNSET);
            this.#branchesCondition[branchIndex].pop();
            this.#branchesIndexChanges.push(branchIndex);
        }
    }

    addEgg() {
        const slotIndex = 0;
        const branchIndex = getRandomInt(0, SETTINGS.branchesCount - 1);

        this.#branchesCondition[branchIndex][slotIndex] = getRandomElement(EGGS_SET);
        this.#branchesIndexChanges.push(branchIndex);
    }

    checkEggDropTake() {
        this.#branchesCondition.forEach((branch, branchIndex) => {
            const eggType = branch[SETTINGS.slotsCount - 1];

            if (eggType !== EGGS_TYPES.UNSET) {
                // taked
                if (this.#characterPosition === branchIndex) {
                    this.#takenEgg = {
                        branchIndex,
                        eggType
                    };

                    setTimeout(() => {
                        ee.emit('takeEgg');
                    }, 0);
                    this.score += 1;

                    this.checkTakenAnalytics();

                } else {
                    // drop
                    this.#fallenEgg = {
                        branchIndex,
                        eggType
                    };

                    setTimeout(() => {
                        ee.emit('fallenEgg');
                    }, 0);
                    this.livesCount -= 1;
                }
            }
        });
    }

    changeCharacterPosition(branchIndex) {
        if (this.#gameStatus === GAME_STATUSES.PLAY || this.#gameStatus === GAME_STATUSES.BEFORE_PLAY) {
            this.#characterPosition = +branchIndex;
        }
    }

    checkTakenAnalytics() {
        if (this.#analyticsScore <= this.#score) {
            this.#accentEvent = true;
            this.queue.push(() => {
                this.#accentEvent = false;
            });
            this.#analyticsScore += this.#analyticsScorePeriod;
        }
    }

    setIntervalScoreEvent() {
        let checkCmplete = false;

        ANALYTICS.forEach((item) => {
            if (!checkCmplete && this.#score > item.count) {
                analyticsEvent('game', 'game over interval', item.label);
                checkCmplete = true;
            }
        });
    }
}

export default Model;
